import { EsaSessionData } from "@outschool/auth-shared";
import { CookieKeys } from "@outschool/data-schemas";
import {
  FundingProgramAbbreviation,
  getFundingProgramEndDate,
  getFundingProgramWaitPeriod,
} from "@outschool/funding-programs-shared";
import Cookies from "js-cookie";

import { useSession } from "../providers/SessionProvider";
import { EsaSessionType, useTokenContext } from "../providers/TokenProvider";
import { useSelfElectedStateFundingProgram } from "./useSelfElectedStateFundingProgram";
import { getFundingProgramPropertiesByInstition } from "@outschool/funding-programs-shared/dist-cjs/FundingProgramProperties";

export const getEsaSession = (): EsaSessionData => {
  return JSON.parse(Cookies.get(CookieKeys.EsaSession) || "null");
};

export const useIsEsaSessionPresent = () => {
  return !!useSessionType();
};

export const esaCheckoutUrl = (
  orderSlugId: string,
  callbackUrl: string,
  vendorId: string,
  cwUrlFallback?: string,
) => {
  const { cwUrl: cwSessionUrl } = getEsaSession();
  const cwUrl = !!cwSessionUrl ? cwSessionUrl : cwUrlFallback;
  return `${cwUrl}/payby-checkout/?callback=${callbackUrl}/api/cw/order/${orderSlugId}&vendorId=${vendorId}`;
};

export const destroyEsaSession = () => {
  Cookies.remove(CookieKeys.EsaSession);
  Cookies.remove(CookieKeys.EsaSessionType);
};

export const useFundingProgramAbbr = (): string | undefined => {
  const { currentUser } = useSession();
  const sessionType = useSessionType();

  switch (sessionType) {
    case EsaSessionType.MERIT:
      return FundingProgramAbbreviation.KS;
    case EsaSessionType.EMA:
      return FundingProgramAbbreviation.EMA;
    case EsaSessionType.CW:
      const esaSession = getEsaSession();
      const fundingProgram = getFundingProgramPropertiesByInstition(
        esaSession.institution,
      );
      return fundingProgram?.abbreviation ?? esaSession?.shipping?.state;
    case EsaSessionType.MEMBERSHIP:
      // assuming there's only 1 program per family
      return currentUser?.esaMembershipPrograms?.[0];
    default:
      return undefined;
  }
};

export const useEsaWaitPeriod = (): number => {
  const esaState = useFundingProgramAbbr();
  return getFundingProgramWaitPeriod(esaState);
};

export const useEsaEndDate = (ignoreFirstDeadline = false): Date | null => {
  const esaState = useFundingProgramAbbr();
  return getFundingProgramEndDate(esaState, ignoreFirstDeadline);
};

export const useSessionType = (): EsaSessionType | null => {
  const { usesCharter } = useSelfElectedStateFundingProgram();

  const { currentUser } = useSession();
  const { authData } = useTokenContext();
  if (currentUser?.hasEsaMembership) {
    return EsaSessionType.MEMBERSHIP;
  }
  if (Cookies.get("meritKeepSession")) {
    return EsaSessionType.MERIT;
  }
  if (Cookies.get("floridaEMASession") === "true") {
    return EsaSessionType.EMA;
  }
  if (Cookies.get(CookieKeys.EsaSession)) {
    return EsaSessionType.CW;
  }
  // if we are logged in, we have set the requiresMembershipForEnrollment flag
  // so we don't need to rely on localStorage values
  // need to use AuthData because here currentUser is not available
  if (usesCharter() && !authData) {
    return EsaSessionType.SELF_IDENTIFIED_PUBLIC_FUNDING;
  }

  return null;
};
